import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2, Plus, Star, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/api/client";
import type {
  CreateSignatureRequest,
  GroupOut_Listing,
  MailboxGroupAccessOut,
  MailboxResponse,
  MailboxUpdate,
  SignatureOut,
  UpdateSignatureRequest,
} from "@/api/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isAdminOrAbove } from "@/lib/roles";
import { useAuth } from "@/stores/auth";

function MailboxDetailPage() {
  const { mailboxId } = useParams({ from: "/_auth/settings/mailboxes/$mailboxId" });
  const role = useAuth((s) => s.user?.role);
  const adminOrAbove = isAdminOrAbove(role);

  const { data: mailbox, isLoading } = useQuery({
    queryKey: ["mailbox", mailboxId],
    queryFn: () => apiFetch<MailboxResponse>(`/api/mailboxes/${mailboxId}`),
  });

  if (isLoading) {
    return <div className="px-6 py-8 text-sm text-text-muted">Loading mailbox…</div>;
  }
  if (!mailbox) {
    return <div className="px-6 py-8 text-sm text-danger">Mailbox not found.</div>;
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <div className="mb-6">
        <Link
          to="/settings/mailboxes"
          className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to mailboxes
        </Link>
        <h1 className="mt-2 text-xl font-medium tracking-tight">
          {mailbox.display_name || mailbox.email_address}
        </h1>
        <p className="mt-0.5 text-sm text-text-muted">{mailbox.email_address}</p>
      </div>

      <Section title="General">
        <GeneralForm mailbox={mailbox} adminOrAbove={adminOrAbove} />
      </Section>

      <Section title="Signatures">
        <SignaturesSection mailboxId={mailboxId} />
      </Section>

      <Section title="Members">
        <p className="text-sm text-text-muted">
          Manage who sees tickets from this mailbox on the{" "}
          <Link
            to="/settings/mailbox-access"
            search={{ mailbox_id: mailboxId }}
            className="text-accent hover:underline"
          >
            Mailbox access
          </Link>{" "}
          page.
        </p>
      </Section>

      {adminOrAbove && (
        <Section title="Group visibility">
          <MailboxGroupAccessSection mailboxId={mailboxId} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8 rounded-lg border border-border bg-surface p-5">
      <h2 className="mb-4 text-sm font-medium tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

// ---------------- General ----------------

type GeneralValues = {
  display_name: string;
  assignment_mode: "auto" | "rule_based";
  ai_draft_enabled: boolean;
  is_active: boolean;
};

function GeneralForm({
  mailbox,
  adminOrAbove,
}: {
  mailbox: MailboxResponse;
  adminOrAbove: boolean;
}) {
  const qc = useQueryClient();
  const form = useForm<GeneralValues>({
    values: {
      display_name: mailbox.display_name ?? "",
      assignment_mode: mailbox.assignment_mode,
      ai_draft_enabled: mailbox.ai_draft_enabled,
      is_active: mailbox.is_active,
    },
  });

  const mutation = useMutation({
    mutationFn: (body: MailboxUpdate) =>
      apiFetch<MailboxResponse>(`/api/mailboxes/${mailbox.id}`, {
        method: "PATCH",
        json: body,
      }),
    onSuccess: () => {
      toast.success("Mailbox updated");
      void qc.invalidateQueries({ queryKey: ["mailbox", mailbox.id] });
      void qc.invalidateQueries({ queryKey: ["mailboxes"] });
    },
    onError: (e) => {
      const code = e instanceof ApiError ? e.code : undefined;
      if (code === "field_forbidden_for_role") {
        toast.error("This field is admin-only — moderator can only edit display name.");
      } else {
        toast.error(e instanceof ApiError ? e.message : "Could not update mailbox");
      }
    },
  });

  return (
    <form
      onSubmit={form.handleSubmit((v) => {
        const body: MailboxUpdate = { display_name: v.display_name.trim() || null };
        if (adminOrAbove) {
          body.assignment_mode = v.assignment_mode;
          body.ai_draft_enabled = v.ai_draft_enabled;
          body.is_active = v.is_active;
        }
        mutation.mutate(body);
      })}
      className="flex flex-col gap-4"
    >
      <Field
        label="Display name"
        hint="Shown to agents in inbox and used as the From-name on outbound replies."
      >
        <Input
          {...form.register("display_name")}
          placeholder="Support · Acme"
        />
      </Field>
      <Field
        label="Assignment mode"
        hint={adminOrAbove ? "How new tickets get routed to agents." : "Admin only."}
      >
        <Select
          value={form.watch("assignment_mode")}
          onValueChange={(v) =>
            form.setValue("assignment_mode", v as GeneralValues["assignment_mode"])
          }
          disabled={!adminOrAbove}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto (round-robin)</SelectItem>
            <SelectItem value="rule_based">Rule-based</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          {...form.register("ai_draft_enabled")}
          disabled={!adminOrAbove}
          className="h-4 w-4 rounded border-border"
        />
        <span>AI-assisted draft replies {!adminOrAbove && <span className="text-xs text-text-faint">(admin only)</span>}</span>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          {...form.register("is_active")}
          disabled={!adminOrAbove}
          className="h-4 w-4 rounded border-border"
        />
        <span>Mailbox is active {!adminOrAbove && <span className="text-xs text-text-faint">(admin only)</span>}</span>
      </label>
      <div>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </Button>
      </div>
    </form>
  );
}

// ---------------- Signatures ----------------

function SignaturesSection({ mailboxId }: { mailboxId: string }) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: signatures, isLoading } = useQuery({
    queryKey: ["mailbox-signatures", mailboxId],
    queryFn: () =>
      apiFetch<SignatureOut[]>(`/api/mailboxes/${mailboxId}/signatures`),
  });

  const setDefault = useMutation({
    mutationFn: (sigId: string) =>
      apiFetch<SignatureOut>(
        `/api/mailboxes/${mailboxId}/signatures/${sigId}/set-default`,
        { method: "POST" },
      ),
    onSuccess: () => {
      toast.success("Default signature updated");
      void qc.invalidateQueries({ queryKey: ["mailbox-signatures", mailboxId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed"),
  });

  const remove = useMutation({
    mutationFn: (sigId: string) =>
      apiFetch<void>(`/api/mailboxes/${mailboxId}/signatures/${sigId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Signature deleted");
      void qc.invalidateQueries({ queryKey: ["mailbox-signatures", mailboxId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed"),
  });

  if (isLoading) {
    return <p className="text-sm text-text-muted">Loading signatures…</p>;
  }

  return (
    <div className="space-y-3">
      {(signatures ?? []).length === 0 && !creating && (
        <p className="text-sm text-text-muted">
          No signatures yet. Add one — agents will be able to pick it when
          replying.
        </p>
      )}
      {(signatures ?? []).map((sig) =>
        editingId === sig.id ? (
          <SignatureEditCard
            key={sig.id}
            mailboxId={mailboxId}
            signature={sig}
            onClose={() => setEditingId(null)}
          />
        ) : (
          <SignatureCard
            key={sig.id}
            signature={sig}
            onSetDefault={() => setDefault.mutate(sig.id)}
            onDelete={() => remove.mutate(sig.id)}
            onEdit={() => setEditingId(sig.id)}
          />
        ),
      )}
      {creating ? (
        <SignatureCreateCard
          mailboxId={mailboxId}
          onClose={() => setCreating(false)}
        />
      ) : (
        <Button variant="ghost" size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          Add signature
        </Button>
      )}
    </div>
  );
}

function SignatureCard({
  signature,
  onSetDefault,
  onDelete,
  onEdit,
}: {
  signature: SignatureOut;
  onSetDefault: () => void;
  onDelete: () => void;
  onEdit: () => void;
}) {
  return (
    <div className="rounded border border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{signature.name}</span>
          {signature.is_default && (
            <span className="inline-flex items-center gap-1 rounded bg-accent-subtle px-1.5 py-0.5 text-xs text-accent">
              <Star className="h-3 w-3" />
              Default
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!signature.is_default && (
            <Button size="sm" variant="ghost" onClick={onSetDefault} title="Set as default">
              <Star className="h-4 w-4" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} title="Delete">
            <Trash2 className="h-4 w-4 text-danger" />
          </Button>
        </div>
      </div>
      <div
        className="prose prose-sm max-w-none text-text-muted"
        dangerouslySetInnerHTML={{ __html: signature.body_html }}
      />
    </div>
  );
}

function SignatureCreateCard({
  mailboxId,
  onClose,
}: {
  mailboxId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const form = useForm<CreateSignatureRequest>({
    defaultValues: { name: "", body_html: "", is_default: false },
  });
  const mutation = useMutation({
    mutationFn: (body: CreateSignatureRequest) =>
      apiFetch<SignatureOut>(`/api/mailboxes/${mailboxId}/signatures`, {
        method: "POST",
        json: body,
      }),
    onSuccess: () => {
      toast.success("Signature created");
      void qc.invalidateQueries({ queryKey: ["mailbox-signatures", mailboxId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed"),
  });
  return (
    <form
      onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
      className="rounded border border-accent-subtle bg-accent-subtle/30 p-3"
    >
      <div className="mb-2 flex flex-col gap-2">
        <Input
          {...form.register("name", { required: true, maxLength: 80 })}
          placeholder="Tier 1 / Sales / etc."
          autoFocus
        />
        <textarea
          {...form.register("body_html", { required: true, maxLength: 300_000 })}
          rows={4}
          className="flex w-full rounded border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          placeholder="<p>— The Acme Support Team</p>"
        />
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            {...form.register("is_default")}
            className="h-3 w-3 rounded border-border"
          />
          <span>Make this the default for new replies</span>
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          <Check className="h-4 w-4" />
          Create
        </Button>
      </div>
    </form>
  );
}

function SignatureEditCard({
  mailboxId,
  signature,
  onClose,
}: {
  mailboxId: string;
  signature: SignatureOut;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const form = useForm<UpdateSignatureRequest>({
    defaultValues: { name: signature.name, body_html: signature.body_html },
  });
  const mutation = useMutation({
    mutationFn: (body: UpdateSignatureRequest) =>
      apiFetch<SignatureOut>(
        `/api/mailboxes/${mailboxId}/signatures/${signature.id}`,
        { method: "PATCH", json: body },
      ),
    onSuccess: () => {
      toast.success("Signature updated");
      void qc.invalidateQueries({ queryKey: ["mailbox-signatures", mailboxId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed"),
  });
  return (
    <form
      onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
      className="rounded border border-accent-subtle bg-accent-subtle/30 p-3"
    >
      <div className="mb-2 flex flex-col gap-2">
        <Input
          {...form.register("name", { required: true, maxLength: 80 })}
          autoFocus
        />
        <textarea
          {...form.register("body_html", { required: true, maxLength: 300_000 })}
          rows={4}
          className="flex w-full rounded border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </Button>
      </div>
    </form>
  );
}

// ---------------- Helpers ----------------

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

// ---------------- Group access ----------------

function MailboxGroupAccessSection({ mailboxId }: { mailboxId: string }) {
  const qc = useQueryClient();
  const { data: groupsOnMailbox } = useQuery({
    queryKey: ["mailbox-group-access", mailboxId],
    queryFn: () =>
      apiFetch<MailboxGroupAccessOut[]>(
        `/api/mailboxes/${mailboxId}/group-access`,
      ),
  });
  const { data: allGroups } = useQuery({
    queryKey: ["groups"],
    queryFn: () => apiFetch<GroupOut_Listing[]>("/api/groups"),
  });

  const grantedIds = useMemo(
    () => new Set((groupsOnMailbox ?? []).map((g) => g.group_id)),
    [groupsOnMailbox],
  );
  const candidates = useMemo(
    () => (allGroups ?? []).filter((g) => !grantedIds.has(g.id)),
    [allGroups, grantedIds],
  );

  const grant = useMutation({
    mutationFn: (groupId: string) =>
      apiFetch<MailboxGroupAccessOut>(
        `/api/mailboxes/${mailboxId}/group-access`,
        { method: "POST", json: { group_id: groupId } },
      ),
    onSuccess: () => {
      toast.success("Group granted");
      void qc.invalidateQueries({
        queryKey: ["mailbox-group-access", mailboxId],
      });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Could not grant"),
  });

  const revoke = useMutation({
    mutationFn: (groupId: string) =>
      apiFetch<void>(
        `/api/mailboxes/${mailboxId}/group-access/${groupId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      toast.success("Group revoked");
      void qc.invalidateQueries({
        queryKey: ["mailbox-group-access", mailboxId],
      });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Could not revoke"),
  });

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-text-muted">
        Every member of a granted group sees this mailbox's tickets via RLS.
        Manage individual users on the Members tab; this lists groups.
      </p>
      {(groupsOnMailbox ?? []).length === 0 ? (
        <p className="text-xs text-text-muted italic">
          No groups have access yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {(groupsOnMailbox ?? []).map((g) => (
            <li
              key={g.id}
              className="flex items-center justify-between rounded border border-border px-3 py-2"
            >
              <div>
                <span className="text-sm font-medium">{g.group_name}</span>
                <span className="ml-2 text-xs text-text-muted">
                  {g.member_count} member{g.member_count === 1 ? "" : "s"}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => revoke.mutate(g.group_id)}
              >
                <Trash2 className="h-3.5 w-3.5 text-danger" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {candidates.length > 0 ? (
        <div className="flex items-center gap-2">
          <Label className="text-xs text-text-muted shrink-0">
            Add group:
          </Label>
          <Select onValueChange={(v) => grant.mutate(v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Pick a group…" />
            </SelectTrigger>
            <SelectContent>
              {candidates.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <p className="text-xs text-text-faint italic">
          All groups already have access.{" "}
          <Link to="/settings/groups" className="text-accent hover:underline">
            Create more groups
          </Link>
          .
        </p>
      )}
    </div>
  );
}

export const Route = createFileRoute("/_auth/settings/mailboxes/$mailboxId")({
  component: MailboxDetailPage,
});
